import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Input, Message, Checkbox, Dimmer, Loader, Menu } from 'semantic-ui-react';

const blank = value => (value == null || value === '' ? '' : value);

const idOf = f => f.id ?? f.function_id;

// Multi-select picker over the functions library (fetched once, filtered client-side).
export const ManageFunctionsModal = ({
    open,
    setOpen,
    componentId,
    currentFunctions,
    setComponentFunctions,
    getFunctions,
    onSaved,
}) => {
    // Desired set of function ids; survives searches/filtering.
    const [selectedIds, setSelectedIds] = useState(new Set());

    const [search, setSearch] = useState('');
    const [library, setLibrary] = useState([]);
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);

    // Guards against out-of-order library responses (last load wins).
    const requestToken = useRef(0);
    // Seed selections only on the open transition, not when currentFunctions changes mid-edit.
    const wasOpen = useRef(false);

    useEffect(() => {
        if (open && !wasOpen.current) {
            const ids = (currentFunctions ?? []).map(idOf).filter(id => id != null);
            setSelectedIds(new Set(ids));
            setSearch('');
            setShowSelectedOnly(false);
            setErrors([]);
        }
        wasOpen.current = open;
    }, [open, currentFunctions]);

    useEffect(() => {
        if (!open) return;
        const token = (requestToken.current += 1);
        setListLoading(true);
        setListError(false);
        getFunctions()
            .then(res => {
                if (token !== requestToken.current) return;
                setLibrary(res?.data ?? []);
                setListLoading(false);
            })
            .catch(() => {
                if (token !== requestToken.current) return;
                setListError(true);
                setListLoading(false);
            });
    }, [open, getFunctions]);

    const toggle = id => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Merge library + current assignments so an assigned-but-hidden function (absent
    // from the library) still resolves in the Selected view.
    const byId = useMemo(() => {
        const map = new Map();
        (library ?? []).forEach(f => map.set(f.id, f));
        (currentFunctions ?? []).forEach(f => {
            const id = idOf(f);
            if (id != null && !map.has(id)) {
                map.set(id, { id, name: f.name, description: f.description });
            }
        });
        return map;
    }, [library, currentFunctions]);

    const filtered = useMemo(() => {
        const base = showSelectedOnly
            ? Array.from(selectedIds)
                  .map(id => byId.get(id))
                  .filter(Boolean)
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            : library;

        const q = search.trim().toLowerCase();
        if (!q) return base;
        return base.filter(f =>
            [f.name, f.description]
                .filter(Boolean)
                .some(v => v.toLowerCase().includes(q))
        );
    }, [library, search, showSelectedOnly, selectedIds, byId]);

    const handleSave = async () => {
        setIsSubmitting(true);
        setErrors([]);
        try {
            await setComponentFunctions(componentId, Array.from(selectedIds));
            await onSaved();
            setOpen(false);
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to save functions. Please try again.';
            setErrors([message]);
        } finally {
            setIsSubmitting(false);
        }
    };

    const close = () => {
        if (isSubmitting) return;
        setOpen(false);
    };

    return (
        <Modal open={open} onClose={close} size="small" style={{ textAlign: 'left' }}>
            <Modal.Header>Manage Functions</Modal.Header>
            <Modal.Content>
                <Menu pointing secondary size="small" style={{ marginBottom: '0.75rem' }}>
                    <Menu.Item
                        name="All"
                        active={!showSelectedOnly}
                        disabled={isSubmitting}
                        onClick={() => setShowSelectedOnly(false)}
                        data-testid="functions-filter-all"
                    />
                    <Menu.Item
                        active={showSelectedOnly}
                        disabled={isSubmitting}
                        onClick={() => setShowSelectedOnly(true)}
                        data-testid="functions-filter-selected"
                    >
                        Selected ({selectedIds.size})
                    </Menu.Item>
                </Menu>
                <Input
                    fluid
                    icon="search"
                    placeholder="Search functions by name or description..."
                    value={search}
                    onChange={(e, { value }) => setSearch(value)}
                    disabled={isSubmitting}
                    aria-label="Search functions"
                    data-testid="function-search"
                />
                <div style={{ marginTop: '0.5rem', color: '#6b7c85', fontSize: '0.85rem' }}>
                    {selectedIds.size} function{selectedIds.size === 1 ? '' : 's'} selected
                </div>
                {/* Concise screen-reader status (not the whole list). */}
                <div
                    aria-live="polite"
                    style={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                        clip: 'rect(0 0 0 0)',
                    }}
                >
                    {listLoading
                        ? 'Loading functions'
                        : listError
                        ? 'Failed to load functions'
                        : `${filtered.length} function${filtered.length === 1 ? '' : 's'} shown`}
                </div>
                <div
                    style={{
                        position: 'relative',
                        marginTop: '0.75rem',
                        maxHeight: '45vh',
                        overflowY: 'auto',
                        border: '1px solid #e8e8e8',
                        borderRadius: '4px',
                    }}
                >
                    <Dimmer active={listLoading} inverted>
                        <Loader size="small">Loading...</Loader>
                    </Dimmer>
                    {listError ? (
                        <div style={{ padding: '1rem' }}>
                            <Message
                                negative
                                size="small"
                                content="Failed to load functions. Close and reopen to retry."
                            />
                        </div>
                    ) : !listLoading && filtered.length === 0 ? (
                        <div style={{ padding: '1rem', color: '#8c979c', fontStyle: 'italic' }}>
                            {showSelectedOnly && !search.trim()
                                ? 'No functions selected.'
                                : 'No matching functions.'}
                        </div>
                    ) : (
                        filtered.map(f => {
                            const name = blank(f.name) || String(f.id);
                            return (
                                <label
                                    key={f.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '0.75rem',
                                        padding: '0.5rem 0.75rem',
                                        borderBottom: '1px solid #f2f2f2',
                                        cursor: isSubmitting ? 'default' : 'pointer',
                                    }}
                                >
                                    <Checkbox
                                        checked={selectedIds.has(f.id)}
                                        onChange={() => toggle(f.id)}
                                        disabled={isSubmitting}
                                        aria-label={name}
                                        data-testid={`function-checkbox-${f.id}`}
                                    />
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ fontWeight: 600, color: '#254a5d' }}>
                                            {name}
                                        </div>
                                        <div style={{ color: '#6b7c85', fontSize: '0.85rem' }}>
                                            {blank(f.description)}
                                        </div>
                                        {blank(f.formula) && (
                                            <div
                                                style={{
                                                    marginTop: '0.25rem',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.8rem',
                                                    color: '#445',
                                                    background: '#f5f7f8',
                                                    border: '1px solid #e8e8e8',
                                                    borderRadius: '3px',
                                                    padding: '0.2rem 0.4rem',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {f.formula}
                                            </div>
                                        )}
                                    </div>
                                </label>
                            );
                        })
                    )}
                </div>
                {errors.length > 0 && (
                    <div style={{ marginTop: '0.75rem' }}>
                        <Message error list={errors} />
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={close} disabled={isSubmitting} data-testid="manage-functions-cancel">
                    Cancel
                </Button>
                <Button
                    className="ui primary button"
                    onClick={handleSave}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    data-testid="manage-functions-save"
                >
                    Save
                </Button>
            </Modal.Actions>
        </Modal>
    );
};
